import { Edge, Node } from '@xyflow/react';
import { BaseNodeData, NodeData } from './WorkflowBuilder.types';
import { getTerminalSubgraph } from './terminalWorkflowGraph';

/** Synthetic node on the main canvas only: incoming edges to the terminal decision are drawn here. Not persisted. */
export const TERMINAL_FLOW_BRIDGE_ID = '__terminal_flow_bridge__';

export function isTerminalFlowBridgeId(id: string): boolean {
    return id === TERMINAL_FLOW_BRIDGE_ID;
}

export function getTerminalSubgraphNodeIds(
    nodes: Node<NodeData>[],
    edges: Edge<NodeData>[],
    terminalDecisionNodeId: string | null
): Set<string> {
    if (!terminalDecisionNodeId) return new Set();
    return new Set(getTerminalSubgraph(nodes, edges, terminalDecisionNodeId).nodes.map((n) => n.id));
}

const bridgeDataNoop = (): void => {};

/** Stable `data` so React Flow does not treat the bridge as updated every projection. */
const TERMINAL_BRIDGE_DATA: BaseNodeData = {
    label: '',
    subLabel: '',
    onDelete: bridgeDataNoop,
};

/**
 * Single shared node instance for the main canvas. Recreating the bridge each render made React Flow
 * lose measured dimensions, re-run ResizeObserver, and toggle wrapper `visibility` (hidden → visible) in a loop.
 * Fixed width/height matches the terminal-bridge node component so dimensions exist on first paint.
 */
export const TERMINAL_BRIDGE_NODE: Node<NodeData> = {
    id: TERMINAL_FLOW_BRIDGE_ID,
    type: 'terminalBridgeNode',
    position: { x: 60, y: 60 },
    width: 64,
    height: 32,
    draggable: false,
    selectable: false,
    deletable: false,
    data: TERMINAL_BRIDGE_DATA,
};

export type DualCanvasProjection = {
    mainNodes: Node<NodeData>[];
    mainEdges: Edge<NodeData>[];
    terminalNodes: Node<NodeData>[];
    terminalEdges: Edge<NodeData>[];
    terminalIds: Set<string>;
};

/**
 * Splits unified graph into two React Flow views. Main view replaces edges into the terminal
 * decision with edges into {@link TERMINAL_FLOW_BRIDGE_ID} so the decision only appears on the terminal canvas.
 */
export function buildDualCanvasProjection(
    nodes: Node<NodeData>[],
    edges: Edge<NodeData>[],
    terminalDecisionNodeId: string | null
): DualCanvasProjection {
    const terminalIds = getTerminalSubgraphNodeIds(nodes, edges, terminalDecisionNodeId);
    const hasTerminalFlow = Boolean(terminalDecisionNodeId && terminalIds.size > 0);

    const mainNodesRaw = nodes.filter((n) => !terminalIds.has(n.id));
    const terminalNodes = nodes.filter((n) => terminalIds.has(n.id));

    if (!hasTerminalFlow || !terminalDecisionNodeId) {
        const mainEdges: Edge<NodeData>[] = [];
        for (const e of edges) {
            if (terminalIds.has(e.source) && terminalIds.has(e.target)) continue;
            mainEdges.push(e);
        }
        return {
            mainNodes: mainNodesRaw,
            mainEdges,
            terminalNodes,
            terminalEdges: edges.filter((e) => terminalIds.has(e.source) && terminalIds.has(e.target)),
            terminalIds,
        };
    }

    const mainNodes = [...mainNodesRaw, TERMINAL_BRIDGE_NODE];
    const mainEdges: Edge<NodeData>[] = [];

    for (const e of edges) {
        const srcT = terminalIds.has(e.source);
        const tgtT = terminalIds.has(e.target);
        if (srcT && tgtT) {
            continue;
        }
        if (!srcT && tgtT) {
            if (e.target === terminalDecisionNodeId) {
                mainEdges.push({
                    ...e,
                    id: e.id || `e_${e.source}_${TERMINAL_FLOW_BRIDGE_ID}`,
                    target: TERMINAL_FLOW_BRIDGE_ID,
                });
            }
            continue;
        }
        if (srcT && !tgtT) {
            continue;
        }
        mainEdges.push(e);
    }

    const terminalEdges = edges.filter((e) => terminalIds.has(e.source) && terminalIds.has(e.target));

    return { mainNodes, mainEdges, terminalNodes, terminalEdges, terminalIds };
}
